import React from 'react'
import { useMemo, FC, MouseEvent, CSSProperties } from 'react'
import moment from 'moment'
import type { EventContextMenuItemsGetter } from '../../utils/EventActions'

import styles from './style.module.scss'
import EventCell from '../EventCell'
import i18n from 'i18n'

interface Todo {
  status?: string
}

interface CalendarInstance {
  id: string
  subject: string | null
  color: string
  folderColor: string
  isBusy: boolean
  isCanceled: boolean
  isDeclined: boolean
  isMeeting: boolean
  myMeetingResponse: string | null
  needsMyResponse: boolean
  isMyEvent: boolean
  allday: boolean
  endDate: Date
  todo?: Todo
}

interface EventCellData {
  instanceId?: string
  title: string
  color: string
  folderColor: string
  isFree: boolean
  isCanceled: boolean
  isDeclined: boolean
  isNotResponse: boolean
  allday: boolean
  date: Date
  endDate: Date
  todo?: Todo
  isTodo: boolean
  isMyEvent: boolean
  style: CSSProperties
  key: string
  isOverCount?: boolean
  overCountId?: string
}

interface DayAlldayProps {
  allday: CalendarInstance[]
  date: Date
  onEventClick: (event: EventCellData, cellElement: HTMLDivElement | null) => void
  onDoubleClick: (e: MouseEvent<HTMLDivElement>) => void
  onEventDoubleClick: (event: EventCellData, cellElement: HTMLDivElement | null) => void
  getEventContextMenuItems?: EventContextMenuItemsGetter | null
  readOnly?: boolean
}

const DayAllday: FC<DayAlldayProps> = ({
  allday,
  date,
  onEventClick,
  onDoubleClick,
  onEventDoubleClick,
  getEventContextMenuItems,
  readOnly = false,
}) => {
  const MAX_VISIBLE_ALLDAY_EVENTS = 3
  const ALLDAY_EVENT_HEIGHT = 24
  const ALLDAY_CONTAINER_BASE_HEIGHT = 0

  const { alldayEventCells, alldayContainerHeight } = useMemo(() => {
    if (!allday || allday.length === 0) {
      return {
        alldayEventCells: [],
        alldayContainerHeight: ALLDAY_CONTAINER_BASE_HEIGHT,
      }
    }

    const showOverCount = allday.length > MAX_VISIBLE_ALLDAY_EVENTS
    let overCount = allday.length - 2
    const alldayEventCells = allday
      .map((event, index): EventCellData | null => {
        if (showOverCount) {
          if (index > 3) {
            return null
          }
          if (index === 2) {
            return {
              style: {
                position: 'absolute',
                top: `${2 + index * ALLDAY_EVENT_HEIGHT}px`,
                left: '4px',
                right: '4px',
                height: '20px',
              },
              title: '+' + overCount,
              key: event.id + '_' + 'overCount',
              isOverCount: true,
              date: new Date(date),
              color: 'lightgray',
              folderColor: 'lightgray',
              overCountId: 'over-count-' + moment(date).format('YYYY-MM-DD'),
              isFree: false,
              isCanceled: false,
              isDeclined: false,
              isNotResponse: false,
              allday: true,
              endDate: event.endDate,
              isTodo: false,
              isMyEvent: false,
            }
          }
        }

        let title = event.subject
        if (title == null || title.length === 0) {
          title = i18n.t('editor.value.noSubject')
        }
        if (event.todo == null) {
          title = (event.isCanceled ? 'Canceled: ' : '') + title
        }

        return {
          instanceId: event.id,
          title: title,
          color: event.color,
          folderColor: event.folderColor,
          isFree: !event.isBusy,
          isCanceled: event.isCanceled,
          isDeclined: event.isDeclined,
          isNotResponse: event.needsMyResponse,
          allday: true,
          date: new Date(date),
          endDate: event.endDate,
          todo: event.todo,
          isTodo: event.todo != null,
          isMyEvent: event.isMyEvent,
          style: {
            position: 'absolute',
            top: `${2 + index * ALLDAY_EVENT_HEIGHT}px`,
            left: '4px',
            right: '4px',
            height: '20px',
          },
          key: `allday-${event.id}`,
        }
      })
      .filter((cell): cell is EventCellData => cell !== null)

    const visibleEvents = Math.min(alldayEventCells.length, MAX_VISIBLE_ALLDAY_EVENTS)
    const containerHeight =
      visibleEvents === 0
        ? ALLDAY_CONTAINER_BASE_HEIGHT
        : ALLDAY_CONTAINER_BASE_HEIGHT + visibleEvents * ALLDAY_EVENT_HEIGHT + 2

    return {
      alldayEventCells,
      alldayContainerHeight: containerHeight,
    }
  }, [allday, date])

  if (alldayEventCells.length === 0) {
    return null
  }

  return (
    <div
      className={styles.alldayContainer}
      style={{ height: `${alldayContainerHeight}px` }}
      onDoubleClick={onDoubleClick}
    >
      {alldayEventCells.slice(0, MAX_VISIBLE_ALLDAY_EVENTS).map((event) => (
        <EventCell
          key={event.key}
          {...({
            overCountId: event.overCountId,
            event: event,
            onCellClick: onEventClick,
            onCellDoubleClick: onEventDoubleClick,
            getContextMenuItems: getEventContextMenuItems,
            readOnly: readOnly,
          } as any)}
        />
      ))}
    </div>
  )
}

export default DayAllday
